import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  TextField,
  Chip,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import dayjs from 'dayjs';
import { TransactionModal } from '../../../components/Modal';

const OtherTab = ({ orders, date, dispatch, me, actions, setDate }) => {
  const [typeFilter, setTypeFilter] = useState('all');

  const otherOrders = useMemo(
    () =>
      orders?.filter((item) =>
        ['รายจ่าย', 'รายรับ'].includes(item?.typeTansaction),
      ) || [],
    [orders],
  );

  const transactionTypes = useMemo(
    () =>
      [...new Set(otherOrders.map((order) => order.typeTansaction))].filter(
        Boolean,
      ),
    [otherOrders],
  );

  const filteredOrders = useMemo(
    () =>
      typeFilter === 'all'
        ? otherOrders
        : otherOrders.filter((order) => order.typeTansaction === typeFilter),
    [otherOrders, typeFilter],
  );

  const totals = useMemo(
    () =>
      filteredOrders.reduce(
        (acc, order) => ({
          totalAmount:
            acc.totalAmount +
              (order.typeTansaction === 'รายรับ'
                ? order.amount
                : -order.amount) || 0,
          count: acc.count + 1,
        }),
        { totalAmount: 0, count: 0 },
      ),
    [filteredOrders],
  );

  const formatDate = (dateString) =>
    dayjs(dateString).format('DD/MM/YYYY HH:mm');

  const DateNavigation = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        p: { xs: 1.5, sm: 2 },
        backgroundColor: '#f5f5f5',
        borderRadius: 2,
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        gap: { xs: 1, sm: 0 },
      }}
    >
      <Button
        variant="outlined"
        startIcon={<ArrowCircleLeftIcon />}
        onClick={() =>
          setDate(dayjs(date).subtract(1, 'day').format('YYYY-MM-DD'))
        }
        sx={{
          fontSize: { xs: '0.875rem', sm: '1rem' },
          fontWeight: 'bold',
          px: { xs: 1.5, sm: 2 },
        }}
      >
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
          วันก่อนหน้า
        </Box>
        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
          ก่อน
        </Box>
      </Button>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 'bold',
          color: '#1976d2',
          fontSize: { xs: '1rem', sm: '1.25rem' },
          textAlign: 'center',
          order: { xs: 3, sm: 0 },
          width: { xs: '100%', sm: 'auto' },
          mt: { xs: 1, sm: 0 },
        }}
      >
        {dayjs(date).format('DD MMMM YYYY')}
      </Typography>
      <Button
        variant="outlined"
        endIcon={<ArrowCircleRightIcon />}
        onClick={() => setDate(dayjs(date).add(1, 'day').format('YYYY-MM-DD'))}
        sx={{
          fontSize: { xs: '0.875rem', sm: '1rem' },
          fontWeight: 'bold',
          px: { xs: 1.5, sm: 2 },
        }}
      >
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
          วันถัดไป
        </Box>
        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
          ถัดไป
        </Box>
      </Button>
    </Box>
  );

  const FilterControls = () => (
    <Card sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
      <Typography
        variant="h6"
        sx={{
          mb: 2,
          fontWeight: 'bold',
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
        }}
      >
        เลือกวันที่และกรองข้อมูล
      </Typography>
      <DateNavigation />
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: { xs: 'stretch', md: 'center' },
        }}
      >
        <Box sx={{ flex: { xs: 'none', md: 1 } }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              label="เลือกวันที่"
              inputFormat="DD/MM/YYYY"
              value={dayjs(date)}
              onChange={(newValue) => setDate(newValue.format('YYYY-MM-DD'))}
              renderInput={(props) => <TextField {...props} fullWidth />}
            />
          </LocalizationProvider>
        </Box>
        <Box sx={{ flex: { xs: 'none', md: 1 } }}>
          <FormControl fullWidth>
            <InputLabel>กรองตามประเภทธุรกรรม</InputLabel>
            <Select
              value={typeFilter}
              label="กรองตามประเภทธุรกรรม"
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="all">ทั้งหมด</MenuItem>
              {transactionTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {/* <Box sx={{ flexShrink: 0, alignSelf: { xs: 'center', md: 'center' } }}>
          <TransactionModal
            date={date}
            me={me}
            dispatch={dispatch}
            actions={actions}
            fetchData={() => dispatch(actions.paymentOtherAll({ date }))}
          />
        </Box> */}
      </Box>
    </Card>
  );

  const SummaryCard = () => (
    <Card sx={{ p: 3, mb: 3, bgcolor: '#f3e5f5', boxShadow: 2 }}>
      <Typography
        variant="h5"
        sx={{ mb: 3, fontWeight: 'bold', textAlign: 'center' }}
      >
        สรุปรายการธุรกรรมอื่นๆ
      </Typography>
      <Grid container spacing={3}>
        {[
          {
            label: 'จำนวนรายการ',
            value: totals.count || 0,
            unit: 'รายการ',
            color: '#1976d2',
          },
          {
            label: 'จำนวนประเภทธุรกรรม',
            value: transactionTypes.length || 0,
            unit: 'ประเภท',
            color: '#ff9800',
          },
          {
            label: 'มูลค่ารวม (สุทธิ)',
            value: (totals.totalAmount || 0).toLocaleString(),
            unit: 'บาท',
            color: totals.totalAmount >= 0 ? '#4caf50' : '#f44336',
          },
        ].map((item, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Box
              sx={{
                textAlign: 'center',
                p: 2,
                backgroundColor: '#fff',
                borderRadius: 2,
              }}
            >
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                {item.label}
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontWeight: 'bold', color: item.color }}
              >
                {item.value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.unit}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Card>
  );

  const OrderTable = () => (
    <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: '#f3e5f5' }}>
            {[
              'ลำดับ',
              'ประเภทธุรกรรม',
              'รายละเอียด',
              'ผู้ทำรายการ',
              'จำนวนเงิน',
              'วันที่ดำเนินการ',
            ].map((header) => (
              <TableCell
                key={header}
                sx={{ fontWeight: 'bold', fontSize: '1.1rem', py: 2 }}
              >
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredOrders.length > 0 ? (
            filteredOrders.map((row, index) => (
              <TableRow
                key={row._id}
                sx={{ '&:nth-of-type(odd)': { bgcolor: '#fafafa' } }}
              >
                <TableCell sx={{ fontSize: '1rem', py: 2, fontWeight: 'bold' }}>
                  {index + 1}
                </TableCell>
                <TableCell sx={{ fontSize: '1rem', py: 2 }}>
                  <Chip
                    label={row?.typeTansaction || 'ไม่ระบุ'}
                    color="primary"
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ fontSize: '1rem', py: 2 }}>
                  <Typography variant="body1">
                    {row?.paymentlist || 'ไม่ระบุ'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontSize: '1rem', py: 2 }}>
                  <Typography variant="body1">
                    {row?.user
                      ? `${row.user.firstname} ${row.user.lastname}`
                      : 'ไม่ระบุ'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontSize: '1rem', py: 2 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 'bold',
                      color:
                        row?.typeTansaction === 'รายรับ'
                          ? '#4caf50'
                          : '#f44336',
                    }}
                  >
                    {row?.typeTansaction === 'รายรับ' ? '+' : '-'}
                    {(row?.amount || 0).toLocaleString()} บาท
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontSize: '1rem', py: 2 }}>
                  <Typography variant="body1">
                    {formatDate(row?.createdAt)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="text.secondary">
                  ไม่มีข้อมูลในวันที่เลือก
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <FilterControls />
      <SummaryCard />
      <OrderTable />
    </Box>
  );
};

OtherTab.propTypes = {
  orders: PropTypes.array,
  date: PropTypes.string.isRequired,
  setDate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  me: PropTypes.object,
  actions: PropTypes.object.isRequired,
};

OtherTab.defaultProps = {
  orders: [],
};

export default OtherTab;
